import React from 'react';
import { themeColor } from '../utils/themeColor';
import './LoginBrandCollage.css';

/**
 * Decorative montage of RiskLo dashboard cards for the sign-in brand panel —
 * the "scattered product cards" treatment.
 *
 * Built as inline SVG/DOM rather than screenshots so it stays crisp at any size,
 * needs no auth to capture, and follows the app's own palette. The card types
 * and labels mirror real surfaces (Best/Worst Day, Net Total, Total Win Rate,
 * daily cumulative P&L, weekday win rates, benchmark comparison).
 *
 * The figures are ILLUSTRATIVE, not live results — see the caption rendered
 * beneath the collage on the login page. Keep it that way: this sits on a public
 * page, and specific numbers here would otherwise read as performance claims.
 *
 * aria-hidden: purely decorative, so it stays out of the accessibility tree.
 */

const GREEN = '#22c55e';
const RED = '#ef4444';
const INDIGO_HEX = '#818cf8';
const VIOLET_HEX = '#8b5cf6';

/** Smooth-ish upward equity curve. */
function EquityCurve() {
  const pts = [0, 8, 5, 14, 11, 20, 17, 26, 23, 34, 31, 42, 46, 43, 54, 58, 55, 66, 72, 70, 80];
  const w = 190;
  const h = 56;
  const max = Math.max(...pts);
  const step = w / (pts.length - 1);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (p / max) * h).toFixed(1)}`).join(' ');
  const area = `${line} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="lbc-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lbcArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={themeColor(VIOLET_HEX)} stopOpacity="0.45" />
          <stop offset="100%" stopColor={themeColor(VIOLET_HEX)} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#lbcArea)" />
      <path d={line} fill="none" stroke={themeColor(VIOLET_HEX)} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Win-rate donut. */
function WinRateDonut({ pct = 53 }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 70 70" className="lbc-donut">
      <circle cx="35" cy="35" r={r} fill="none" stroke="rgba(148,163,184,0.22)" strokeWidth="9" />
      <circle
        cx="35" cy="35" r={r} fill="none" stroke={GREEN} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 35 35)"
      />
      <text x="35" y="39" textAnchor="middle" className="lbc-donut-text">{pct}%</text>
    </svg>
  );
}

/** Mon–Fri bars, mixed positive/negative. */
function WeekdayBars() {
  const days = [
    { d: 'M', v: 0.75 }, { d: 'T', v: 1 }, { d: 'W', v: 0.45 },
    { d: 'T', v: -0.3 }, { d: 'F', v: 0.62 },
  ];
  return (
    <div className="lbc-bars">
      {days.map((x, i) => (
        <div className="lbc-bar-col" key={i}>
          <div className="lbc-bar-track">
            <div
              className="lbc-bar-fill"
              style={{
                height: `${Math.abs(x.v) * 100}%`,
                background: x.v >= 0 ? GREEN : RED,
                opacity: 0.85,
              }}
            />
          </div>
          <span className="lbc-bar-label">{x.d}</span>
        </div>
      ))}
    </div>
  );
}

export default function LoginBrandCollage() {
  return (
    <div className="lbc" aria-hidden="true">
      {/* Back layer — benchmark comparison */}
      <div className="lbc-card lbc-card--compare">
        <div className="lbc-card-title">Growth of $10,000</div>
        <EquityCurve />
        <div className="lbc-legend">
          <span><i style={{ background: themeColor(VIOLET_HEX) }} />Strategy</span>
          <span><i style={{ background: RED }} />S&amp;P 500</span>
        </div>
      </div>

      {/* Net total */}
      <div className="lbc-card lbc-card--net">
        <div className="lbc-card-label">Net Total</div>
        <div className="lbc-card-value lbc-pos">$13,249</div>
        <div className="lbc-card-sub">
          <span className="lbc-pos">Wins $37,584</span>
          <span className="lbc-neg">Losses −$24,335</span>
        </div>
      </div>

      {/* Best / worst day */}
      <div className="lbc-card lbc-card--day">
        <div className="lbc-card-label">Best / Worst Day</div>
        <div className="lbc-card-value">
          <span className="lbc-pos">$630</span>
          <span className="lbc-divider">/</span>
          <span className="lbc-neg">−$365</span>
        </div>
      </div>

      {/* Win rate */}
      <div className="lbc-card lbc-card--rate">
        <div className="lbc-card-label">Total Win Rate</div>
        <WinRateDonut pct={53} />
      </div>

      {/* Weekday win rates */}
      <div className="lbc-card lbc-card--weekday">
        <div className="lbc-card-label">Weekday P&amp;L</div>
        <WeekdayBars />
      </div>

      {/* Strategy pills */}
      <div className="lbc-card lbc-card--pills">
        {/* Generic names: this panel shows on every brand's sign-in page. */}
        {['Multi-Strat', 'Trend', 'Breakout'].map((n, i) => (
          <span className="lbc-pill" key={n} style={{ borderColor: [themeColor(INDIGO_HEX), GREEN, themeColor(VIOLET_HEX)][i] }}>
            <i style={{ background: [themeColor(INDIGO_HEX), GREEN, themeColor(VIOLET_HEX)][i] }} />{n}
          </span>
        ))}
      </div>
    </div>
  );
}
